import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Chart } from '@antv/g2';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../theme/ThemeContext';
import StackedBarChart from '../../atoms/StackedBarChart/StackedBarChart';
import ChartNoDataState from '../../atoms/ChartNoDataState/ChartNoDataState';
import Slider from '../../atoms/Slider/Slider';
import Select from '../../atoms/Select/Select';
import Button from '../../atoms/Button/Button';
import DownloadFile from '../../atoms/Icon/DownloadFile';
import ArrowRight from '../../atoms/Icon/ArrowRight';
import ChartLegendOverflow from '../ChartLegendOverflow/ChartLegendOverflow';
import { ChartConfig } from '../../../utils/BaseChart/BaseChartOptions';
import {
    formatNumWithKMB,
    formatNumWithKMBTwoDecimalPlaces,
    formatXAxisLabel,
    CHART_FONT_FAMILY_CSS,
} from '../../../utils/formatters';
import {
    getAggregatedChartTooltipInteraction,
    getChartColumnHighlightInteraction,
} from '../../../utils/chartAggregatedTooltip';
import { BAR_VALUE_LABEL_DOMAIN_PADDING, computeSingleAxisDomain } from '../../../utils/chartAxisAlignment';
import { useStackedBarChartFilters, type StackedBarChartRow } from './useStackedBarChartFilters';
import { EnhancedStackedBarChartProps } from './EnhancedStackedBarChart.types';
import ChartFilterBar, { ChartFilterColumn, CHART_FILTER_SELECT_CLASSNAME } from '../EnhancedBarChart/ChartFilterBar';
import { chartMultiSelectTagRender } from '../EnhancedBarChart/chartFilterMultiTagRender';

type StackedBarChartRowWithTotals = StackedBarChartRow & {
    stackTotal: number;
    showTotalLabel: boolean;
    /** When true, total label is placed below the stack (negative total); otherwise above (positive / zero). */
    totalMarkerBelow: boolean;
};

/** Add per-row stack total and which segment anchors the total label (one label per category). */
function enrichRowsWithStackTotals(rows: StackedBarChartRow[]): StackedBarChartRowWithTotals[] {
    if (rows.length === 0) return [];

    const byCategory = new Map<string, StackedBarChartRow[]>();
    for (const row of rows) {
        const list = byCategory.get(row.category) ?? [];
        list.push(row);
        byCategory.set(row.category, list);
    }

    const totalByCategory = new Map<string, number>();
    const totalLabelSegmentIndexByCategory = new Map<string, number>();

    for (const [category, group] of byCategory) {
        const values = group.map((r) => r.value);
        const stackTotal = values.reduce((a, b) => a + b, 0);
        totalByCategory.set(category, stackTotal);

        let cum = 0;
        const prefixes: number[] = [];
        for (const v of values) {
            cum += v;
            prefixes.push(cum);
        }
        if (prefixes.length === 0) {
            totalLabelSegmentIndexByCategory.set(category, -1);
            continue;
        }
        // G2 stackY stacks negative segments in *reverse* series order (see stackY NG.reverse()).
        // The bar's lowest point is the bottom of the first negative segment in data order (NG[0]),
        // not the segment where a forward cumulative sum hits min(prefixes).
        let segIdx = -1;
        if (stackTotal < 0) {
            const firstNegIdx = values.findIndex((v) => v < 0);
            segIdx = firstNegIdx >= 0 ? firstNegIdx : values.length - 1;
        } else {
            const outer = Math.max(...prefixes);
            for (let i = 0; i < prefixes.length; i++) {
                if (Math.abs(prefixes[i] - outer) < 1e-9) segIdx = i;
            }
            if (segIdx < 0) segIdx = prefixes.length - 1;
        }
        totalLabelSegmentIndexByCategory.set(category, segIdx);
    }

    const indexWithinCategory = new Map<string, number>();
    return rows.map((row) => {
        const idx = indexWithinCategory.get(row.category) ?? 0;
        indexWithinCategory.set(row.category, idx + 1);
        const labelSegIdx = totalLabelSegmentIndexByCategory.get(row.category) ?? -1;
        const stackTotal = totalByCategory.get(row.category) ?? 0;
        return {
            ...row,
            stackTotal,
            totalMarkerBelow: stackTotal < 0,
            showTotalLabel: idx === labelSegIdx && labelSegIdx >= 0,
        };
    });
}

const STACK_BAR_COLORS = [
    '#5B50D1',
    '#DBC7A4',
    '#4ECDC4',
    '#FF6B6B',
    '#95E1D3',
    '#F38181',
    '#AA96DA',
    '#FCBAD3',
    '#A8D8EA',
    '#FFD93D',
];

/**
 * Extra top canvas + y-domain headroom so `overlapHide` can still collapse adjacent bar labels
 * without the tallest stack’s label reading as overlapping the plot / chart boundary (varies by DPR & font metrics).
 */
const STACKED_BAR_LABEL_PADDING_TOP = 20;
const STACKED_BAR_LABEL_INSET_TOP = 40;
/** Headroom baked into pinned `domain` (labels + tallest stack/total). Mirrors prior view `scale.y.padding`. */
const STACKED_BAR_Y_AXIS_DOMAIN_PADDING = Math.min(0.38, BAR_VALUE_LABEL_DOMAIN_PADDING + 0.2);

/**
 * Mirrors G2 v5 `stackY` transform: each category splits segments into negatives vs non-negatives;
 * negatives stack downward from `start`, positives stack upward from `start` (negative layers in
 * reverse series index order). Sequential “running sum” extent is wrong for mixed-sign stacks and
 * clips the downward stack relative to y=0 / the x-axis.
 */
function g2AlignedStackExtentFromSegmentValues(values: number[], start = 0): { min: number; max: number } {
    let lo = start;
    let hi = start;
    const PG: number[] = [];
    const NG: number[] = [];
    values.forEach((raw, i) => {
        const y = Number(raw);
        if (!Number.isFinite(y)) return;
        if (y < 0) NG.push(i);
        else PG.push(i);
    });

    let ny = start;
    for (let k = NG.length - 1; k >= 0; k--) {
        const i = NG[k];
        const y = Number(values[i]);
        const bottom = ny;
        ny += y;
        lo = Math.min(lo, bottom, ny);
        hi = Math.max(hi, bottom, ny);
    }

    let py = start;
    for (const i of PG) {
        const y = Number(values[i]);
        if (y > 0) {
            const bottom = py;
            py += y;
            lo = Math.min(lo, bottom, py);
            hi = Math.max(hi, bottom, py);
        } else {
            lo = Math.min(lo, py);
            hi = Math.max(hi, py);
        }
    }

    return { min: lo, max: hi };
}

const EnhancedStackedBarChart: React.FC<EnhancedStackedBarChartProps> = (props) => {
    const {
        vizData,
        title: propTitle,
        description,
        showFilters = true,
        onDownload,
        onBarClick,
        onTrackChartEvent,
        sizeAttributes,
        footerLabel,
        showFooterArrow = true,
        className = '',
        chartClassName = '',
    } = props;

    const xAxisTypeToken = vizData?.xAxisType?.[0]?.trim();
    const xAxisScrollKind = xAxisTypeToken?.toLowerCase();
    const scrollModeFromViz =
        vizData && (xAxisScrollKind === 'category' || xAxisScrollKind === 'time') ? xAxisScrollKind : null;

    const showSlider =
        props.showSlider !== undefined
            ? props.showSlider
            : scrollModeFromViz === 'category'
              ? false
              : scrollModeFromViz === 'time'
                ? true
                : true;

    const showScrollbar =
        props.showScrollbar !== undefined
            ? props.showScrollbar
            : scrollModeFromViz === 'category'
              ? true
              : scrollModeFromViz === 'time'
                ? false
                : false;

    const scrollbarVisibleCount = props.scrollbarVisibleCount ?? 10;

    const theme = useTheme();
    const chartFilters = useStackedBarChartFilters(vizData);

    const chartConfig = useMemo(() => {
        if (!vizData || !chartFilters.normalizedVizData) return null;

        const normalized = chartFilters.normalizedVizData;
        const title = normalized.chartTitle || propTitle || '';
        const yAxisLabel = Array.isArray(normalized.yAxisTitle) ? normalized.yAxisTitle[0] : '';
        const xAxisTitleEntry =
            Array.isArray(normalized.xAxisTitle) && normalized.xAxisTitle.length > 0
                ? String(normalized.xAxisTitle[0] ?? '').trim()
                : '';
        const xAxisKey = xAxisTitleEntry || 'category';
        const xAxisTitleLabel = xAxisTitleEntry || undefined;

        const legend = chartFilters.selectedZ.map((label, idx) => ({
            label,
            color: STACK_BAR_COLORS[idx % STACK_BAR_COLORS.length],
        }));

        return {
            title,
            yAxisLabel,
            xAxisKey,
            xAxisTitleLabel,
            data: chartFilters.chartData,
            legend,
        };
    }, [vizData, propTitle, chartFilters.chartData, chartFilters.selectedZ, chartFilters.normalizedVizData]);

    const uniqueCategories = useMemo(() => {
        const rows = chartConfig?.data ?? [];
        const seen = new Set<string>();
        const ordered: string[] = [];
        for (const row of rows) {
            if (!seen.has(row.category)) {
                seen.add(row.category);
                ordered.push(row.category);
            }
        }
        return ordered;
    }, [chartConfig?.data]);
    const totalCategories = uniqueCategories.length;

    const [sliderRange, setSliderRange] = useState<[number, number]>([0, Math.max(0, totalCategories - 1)]);

    useEffect(() => {
        setSliderRange([0, Math.max(0, totalCategories - 1)]);
    }, [totalCategories]);

    const sliderFilteredCategories = useMemo(
        () => new Set(uniqueCategories.slice(sliderRange[0], sliderRange[1] + 1)),
        [uniqueCategories, sliderRange],
    );

    const filteredCategoryCount = sliderRange[1] - sliderRange[0] + 1;

    /**
     * `scrollbarVisibleCount` is a budget on the *total* visible categories. Stacked
     * bars render exactly one bar per category (each with multiple stacked segments),
     * so the budget maps directly to category count.
     */
    const effectiveScrollbarVisibleCount = Math.max(1, scrollbarVisibleCount);

    const barsConfig: ChartConfig[] = useMemo(() => {
        if (!chartConfig || chartConfig.data.length === 0) return [];

        const colorRange = chartConfig.legend.map((l) => l.color);

        return [
            {
                encode: {
                    x: 'category',
                    y: 'value',
                    color: 'series',
                },
                scale: {
                    color: {
                        range: colorRange,
                        domain: chartConfig.legend.map((l) => l.label),
                    },
                },
                transform: [{ type: 'stackY' }],
                labels: [
                    {
                        text: (d: StackedBarChartRowWithTotals) =>
                            d.showTotalLabel && !d.totalMarkerBelow
                                ? formatNumWithKMBTwoDecimalPlaces(d.stackTotal)
                                : '',
                        position: 'top',
                        // Default 'top' uses textBaseline 'top', which draws text downward into the bar.
                        // Bottom baseline anchors the label on the stack top so the number sits above the bar.
                        textBaseline: 'bottom',
                        dy: -8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                    {
                        text: (d: StackedBarChartRowWithTotals) =>
                            d.showTotalLabel && d.totalMarkerBelow
                                ? formatNumWithKMBTwoDecimalPlaces(d.stackTotal)
                                : '',
                        position: 'bottom',
                        // Top baseline anchors at the stack bottom so the number sits below the bar (symmetric to positive).
                        textBaseline: 'top',
                        dy: 8,
                        fill: 'rgba(0, 0, 0, 0.65)',
                        fontSize: 12,
                        fontFamily: CHART_FONT_FAMILY_CSS,
                        transform: [{ type: 'overlapHide' }],
                    },
                ],
                ...(showScrollbar
                    ? {
                          additionalConfig: {
                              scrollbar: {
                                  x: {
                                      ratio: Math.min(
                                          1,
                                          effectiveScrollbarVisibleCount / Math.max(1, filteredCategoryCount),
                                      ),
                                      trackSize: 8,
                                      thumbFill: '#5B50D1',
                                      thumbFillOpacity: 0.45,
                                      trackFill: '#f0f0f0',
                                      trackFillOpacity: 1,
                                      isRound: true,
                                  },
                              },
                          },
                      }
                    : showSlider
                      ? {
                            additionalConfig: {
                                slider: {
                                    x: {
                                        selectionFill: '#F7F5FF',
                                        selectionFillOpacity: 1,
                                    },
                                },
                            },
                        }
                      : {}),
                tooltip: {
                    title: (d: StackedBarChartRowWithTotals) => String(d.category ?? ''),
                    items: [
                        (d: StackedBarChartRowWithTotals) => {
                            const leg = chartConfig.legend.find((l) => l.label === d.series);
                            return {
                                name: String(d.series ?? ''),
                                value: formatNumWithKMB(d.value),
                                color: leg?.color ?? STACK_BAR_COLORS[0],
                            };
                        },
                    ],
                },
            },
        ];
    }, [chartConfig, showSlider, showScrollbar, effectiveScrollbarVisibleCount, filteredCategoryCount]);

    const { data, yAxisDomain, hasNegativeStackTotals } = useMemo(() => {
        const rows = chartConfig?.data ?? [];
        const enriched = enrichRowsWithStackTotals(rows);

        const segmentsByCategory = new Map<string, number[]>();
        for (const r of enriched) {
            const list = segmentsByCategory.get(r.category) ?? [];
            list.push(Number(r.value));
            segmentsByCategory.set(r.category, list);
        }

        let extentMin = Infinity;
        let extentMax = -Infinity;
        for (const values of segmentsByCategory.values()) {
            const { min, max } = g2AlignedStackExtentFromSegmentValues(values);
            extentMin = Math.min(extentMin, min);
            extentMax = Math.max(extentMax, max);
        }

        if (!Number.isFinite(extentMin) || !Number.isFinite(extentMax)) {
            return { data: enriched, yAxisDomain: undefined, hasNegativeStackTotals: false };
        }

        const hasNeg = extentMin < 0;
        const domain = computeSingleAxisDomain(extentMin, extentMax, STACKED_BAR_Y_AXIS_DOMAIN_PADDING);
        return { data: enriched, yAxisDomain: domain, hasNegativeStackTotals: hasNeg };
    }, [chartConfig?.data]);

    const viewLevelAdditionalProps = useMemo(
        () => ({
            theme: { fontFamily: CHART_FONT_FAMILY_CSS },
            scale: {
                /** Pinned domain from stacked geometry (sums all factors via cumulative extents) so scrolling never shrinks axis to single-segment range. */
                y: {
                    ...(yAxisDomain ? { domain: yAxisDomain } : {}),
                },
            },
            axis: {
                x: {
                    labelFormatter: (text: string) => formatXAxisLabel(text),
                    ...(chartConfig?.xAxisTitleLabel
                        ? {
                              title: chartConfig.xAxisTitleLabel,
                              titleFontFamily: CHART_FONT_FAMILY_CSS,
                              titleFontSize: 14,
                              titleFontWeight: 400,
                              titleFill: 'rgba(0,0,0,0.45)',
                          }
                        : { title: false }),
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                    // Room between plot bottom and category labels when totals sit below bars;
                    // `insetBottom` on the chart view handles label clearance (see sizeAttributes).
                    ...(hasNegativeStackTotals && {
                        crossPadding: 32,
                        labelSpacing: 12,
                    }),
                },
                y: {
                    labelFormatter: (value: number) => formatNumWithKMB(value),
                    title: false,
                    grid: true,
                    gridLineWidth: 1,
                    gridStroke: 'rgba(0, 0, 0, 0.06)',
                    labelFontFamily: CHART_FONT_FAMILY_CSS,
                },
            },
            legend: false,
            interaction: {
                ...getChartColumnHighlightInteraction(),
                tooltip: getAggregatedChartTooltipInteraction(),
                /**
                 * G2's `sliderFilter` / `scrollbarFilter` interactions default to
                 * `adaptiveMode: 'filter'`, which on every scroll/slider change
                 * recomputes the value-axis domain from the data currently visible
                 * inside the scroll window — silently overriding the explicit
                 * `scale.y` settings. Disable adaptive mode so scrolling becomes
                 * a pure pan operation with a stable y-scale.
                 */
                sliderFilter: { adaptiveMode: false },
                scrollbarFilter: { adaptiveMode: false },
            },
            /** See EnhancedBarChart: `sliderFilter` defaults `clip` to true while sliding, which trims labels. */
            clip: false,
        }),
        [yAxisDomain, hasNegativeStackTotals, chartConfig?.xAxisTitleLabel],
    );

    const chartTheme = useMemo(
        () => ({
            ...theme,
            components: {
                ...theme?.components,
                Select: {
                    ...(theme as { components?: { Select?: object } })?.components?.Select,
                    colorTextPlaceholder: 'rgba(0,0,0,0.7)',
                },
            },
        }),
        [theme],
    );

    const chartRef = useRef<Chart | null>(null);
    const chartWrapperRef = useRef<HTMLDivElement | null>(null);
    const touchStartRef = useRef<{ x: number; y: number } | null>(null);
    const scrollOffsetRef = useRef(0);
    const lastScrollTimeRef = useRef(0);
    const SCROLL_THROTTLE_MS = 150;

    const needsScroll = showScrollbar && filteredCategoryCount > effectiveScrollbarVisibleCount;
    const maxScrollOffset = Math.max(0, filteredCategoryCount - effectiveScrollbarVisibleCount);

    const scrollToOffset = useCallback(
        (targetOffset: number) => {
            const chart = chartRef.current;
            if (!chart || !needsScroll) return;

            const next = Math.max(0, Math.min(maxScrollOffset, Math.round(targetOffset)));
            const prev = scrollOffsetRef.current;
            if (next === prev) return;
            scrollOffsetRef.current = next;

            const absStart = sliderRange[0] + next;
            const absEnd = Math.min(absStart + effectiveScrollbarVisibleCount - 1, sliderRange[1]);
            const startCat = uniqueCategories[absStart];
            const endCat = uniqueCategories[absEnd];

            chart.emit('scrollbarX:filter', {
                data: { selection: [[startCat, endCat], undefined] },
            });
        },
        [needsScroll, maxScrollOffset, effectiveScrollbarVisibleCount, sliderRange, uniqueCategories],
    );

    const scrollChart = useCallback(
        (delta: number) => {
            scrollToOffset(scrollOffsetRef.current + delta);
        },
        [scrollToOffset],
    );

    // ===== Analytics helpers =====
    const chartTitleForTracking = vizData?.chartTitle ?? propTitle;
    const chartTypeForTracking = vizData?.chart_type ?? 'StackBar';

    const trackChartEvent = onTrackChartEvent;

    const trackFilterValueChange = useCallback(
        (filter_name: string, prevValues: unknown[], nextValues: unknown[]) => {
            if (!trackChartEvent) return;
            const prev = new Set(prevValues as Array<string | number>);
            const next = new Set(nextValues as Array<string | number>);
            const added = [...next].filter((v) => !prev.has(v));
            const removed = [...prev].filter((v) => !next.has(v));
            if (added.length > 0) {
                trackChartEvent({
                    type: 'filter_select',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: added,
                });
            }
            if (removed.length > 0) {
                trackChartEvent({
                    type: 'filter_deselect',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    filter_name,
                    values: removed,
                });
            }
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const trackFilterDropdownOpen = useCallback(
        (filter_name: string) => {
            trackChartEvent?.({
                type: 'filter_dropdown_open',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                filter_name,
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleSliderChange = useCallback(
        (val: number | number[]) => {
            if (!Array.isArray(val)) return;
            const [start, end] = val as [number, number];
            setSliderRange([start, end]);
            scrollOffsetRef.current = 0;
            trackChartEvent?.({
                type: 'slider_use',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                range: [start, end],
            });
        },
        [trackChartEvent, chartTitleForTracking, chartTypeForTracking],
    );

    const handleChartReady = useCallback((chart: Chart) => {
        chartRef.current = chart;
        scrollOffsetRef.current = 0;
    }, []);

    useEffect(() => {
        if (!needsScroll) return;
        const wrapper = chartWrapperRef.current;
        if (!wrapper) return;

        const onWheel = (e: WheelEvent) => {
            if (!chartRef.current) return;
            e.preventDefault();
            const now = Date.now();
            if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
            lastScrollTimeRef.current = now;
            const rawDelta = e.deltaX || e.deltaY;
            const direction = rawDelta > 0 ? 1 : -1;
            scrollChart(direction);
            trackChartEvent?.({
                type: 'scroll',
                chartTitle: chartTitleForTracking,
                chartType: chartTypeForTracking,
                direction: direction > 0 ? 'forward' : 'backward',
            });
        };

        const onTouchStart = (e: TouchEvent) => {
            if (e.touches.length === 1) {
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchMove = (e: TouchEvent) => {
            if (!touchStartRef.current || e.touches.length !== 1) return;
            const dx = touchStartRef.current.x - e.touches[0].clientX;
            const dy = touchStartRef.current.y - e.touches[0].clientY;
            if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
                e.preventDefault();
                const now = Date.now();
                if (now - lastScrollTimeRef.current < SCROLL_THROTTLE_MS) return;
                lastScrollTimeRef.current = now;
                const direction = dx > 0 ? 1 : -1;
                scrollChart(direction);
                trackChartEvent?.({
                    type: 'scroll',
                    chartTitle: chartTitleForTracking,
                    chartType: chartTypeForTracking,
                    direction: direction > 0 ? 'forward' : 'backward',
                });
                touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
            }
        };

        const onTouchEnd = () => {
            touchStartRef.current = null;
        };

        wrapper.addEventListener('wheel', onWheel, { passive: false });
        wrapper.addEventListener('touchstart', onTouchStart, { passive: true });
        wrapper.addEventListener('touchmove', onTouchMove, { passive: false });
        wrapper.addEventListener('touchend', onTouchEnd, { passive: true });

        return () => {
            wrapper.removeEventListener('wheel', onWheel);
            wrapper.removeEventListener('touchstart', onTouchStart);
            wrapper.removeEventListener('touchmove', onTouchMove);
            wrapper.removeEventListener('touchend', onTouchEnd);
        };
    }, [needsScroll, scrollChart, trackChartEvent, chartTitleForTracking, chartTypeForTracking]);

    if (!vizData || !chartFilters.normalizedVizData) return null;

    const title = chartConfig?.title ?? vizData.chartTitle ?? '';
    const legend = chartConfig?.legend ?? [];
    const yAxisLabel = chartConfig?.yAxisLabel ?? '';
    const hasZFilter = chartFilters.zOptions.length > 0;

    return (
        <ConfigProvider theme={chartTheme}>
            <div
                className={`w-full bg-white rounded-lg border border-[rgba(0,0,0,0.06)] ${className}`}
                data-test={props['data-test']}
                data-test-id={props['data-test-id']}
                data-asper-enhanced-stacked-barchart
            >
                {/* Header Section - same as Bar/Line chart */}
                <div className="p-5 border-b border-[rgba(0,0,0,0.06)]">
                    <div className="flex justify-between items-start mb-2">
                        <h3 className="font-['Open_Sans'] font-semibold text-[16px] leading-normal text-[rgba(0,0,0,0.88)] m-0">
                            {title}
                        </h3>
                        {onDownload && (
                            <Button
                                type="text"
                                icon={<DownloadFile size={16} />}
                                onClick={onDownload}
                                className="!p-2"
                                data-test-id={`${props['data-test-id']}-download-btn`}
                            />
                        )}
                    </div>

                    {description && (
                        <p className="font-['Open_Sans'] font-normal text-[14px] leading-[22px] text-[rgba(0,0,0,0.65)] m-0">
                            {description}
                        </p>
                    )}
                </div>

                {/* Z Filter Section - only filter for stacked bar */}
                {showFilters && hasZFilter && (
                    <div className="px-5 pt-5 pb-3" data-export-exclude>
                        <ChartFilterBar>
                            <ChartFilterColumn label="Factors">
                                <Select
                                    mode="multiple"
                                    value={chartFilters.selectedZ}
                                    onDropdownVisibleChange={(open) => open && trackFilterDropdownOpen('Factors')}
                                    onChange={(value) => {
                                        trackFilterValueChange(
                                            'Factors',
                                            chartFilters.selectedZ,
                                            (value as string[]) || [],
                                        );
                                        chartFilters.updateZFilter(value as string[]);
                                    }}
                                    options={chartFilters.zOptions.map((z: string) => ({
                                        label: z,
                                        value: z,
                                    }))}
                                    tagRender={chartMultiSelectTagRender(chartFilters.selectedZ, (v) => v)}
                                    className={CHART_FILTER_SELECT_CLASSNAME}
                                    placeholder="Select factors..."
                                    data-test-id={`${props['data-test-id']}-filter-z`}
                                />
                            </ChartFilterColumn>
                        </ChartFilterBar>
                    </div>
                )}

                {/* Legend Section — overflow via "+N More" */}
                {legend.length > 0 && (
                    <div className="px-5 py-3 w-full min-w-0">
                        <ChartLegendOverflow
                            items={legend.map((item) => ({
                                key: item.label,
                                label: item.label,
                                color: item.color,
                            }))}
                            data-test-id={props['data-test-id'] ? `${props['data-test-id']}-legend` : undefined}
                        />
                    </div>
                )}

                {/* Y-Axis Label */}
                {yAxisLabel && (
                    <div className="px-5 pt-3">
                        <p className="font-['Open_Sans'] font-normal text-sm leading-[20px] text-[rgba(0,0,0,0.45)] m-0">
                            {yAxisLabel}
                        </p>
                    </div>
                )}

                {/* Chart Section - same container as Bar/Line */}
                <div ref={chartWrapperRef} className={`px-5 py-5 ${chartClassName}`}>
                    {data.length === 0 || barsConfig.length === 0 ? (
                        <ChartNoDataState minHeight={400} data-test-id={`${props['data-test-id']}-no-data`} />
                    ) : (
                        <StackedBarChart
                            data={showScrollbar ? data.filter((d) => sliderFilteredCategories.has(d.category)) : data}
                            barsConfig={barsConfig}
                            legend={[]}
                            onChartReady={showScrollbar ? handleChartReady : undefined}
                            sizeAttributes={{
                                height: 400,
                                paddingTop: STACKED_BAR_LABEL_PADDING_TOP,
                                insetTop: STACKED_BAR_LABEL_INSET_TOP,
                                paddingBottom:
                                    (showSlider && !showScrollbar ? 140 : 100) + (hasNegativeStackTotals ? 64 : 35),
                                /**
                                 * When any stack dips below zero (G2 splits pos/neg stacks at y=0), leave room below
                                 * the drawable area so negative segments / bottom totals clear category tick labels.
                                 */
                                ...(hasNegativeStackTotals && { insetBottom: 44 }),
                                paddingLeft: 80, // Reserve space for y-axis labels (e.g. "100M", "500M") to prevent truncation in constrained panels
                                ...sizeAttributes,
                            }}
                            viewLevelAdditionalProps={viewLevelAdditionalProps}
                            barClickHandler={onBarClick}
                            data-test={`${props['data-test']}-chart`}
                        />
                    )}
                </div>

                {/* Range Slider - filters which bars are included from the full dataset (scrollbar mode) */}
                {showScrollbar && showSlider && data.length > 0 && totalCategories > 1 && (
                    <div className="px-5 pb-1" data-export-exclude>
                        <Slider
                            range
                            min={0}
                            max={totalCategories - 1}
                            value={sliderRange}
                            onChange={handleSliderChange}
                            tooltip={{
                                formatter: (val?: number) =>
                                    val !== undefined && uniqueCategories[val] ? String(uniqueCategories[val]) : '',
                            }}
                            styles={{
                                track: { background: '#5B50D1' },
                                rail: { background: '#f0f0f0' },
                            }}
                            data-test-id={`${props['data-test-id']}-scroll-slider`}
                        />
                    </div>
                )}

                {/* Footer Section */}
                {footerLabel && (
                    <div className="px-5 pb-5 flex justify-center items-center gap-2">
                        <span className="font-['Open_Sans'] font-semibold text-[14px] leading-[22px] text-[rgba(0,0,0,0.88)]">
                            {footerLabel}
                        </span>
                        {showFooterArrow && <ArrowRight size={16} color="rgba(0,0,0,0.88)" />}
                    </div>
                )}
            </div>
        </ConfigProvider>
    );
};

export default EnhancedStackedBarChart;
